import React from "react";

// Shared slider used by the training log form and the Performance flow.
// Extracted verbatim from TrainingLogForm so both surfaces look identical.

export const EFFORT_COLORS = [
  "#10b981",
  "#22c55e",
  "#3b82f6",
  "#60a5fa",
  "#8b5cf6",
  "#a78bfa",
  "#f97316",
  "#fb923c",
  "#ef4444",
  "#dc2626",
  "#b91c1c",
];

export const getEffortColor = (value: number) =>
  EFFORT_COLORS[Math.min(Math.floor(value), 10)] || EFFORT_COLORS[0];

export const RESPIRATION_DESCRIPTIONS: Record<number, string> = {
  0: "Easy at rest",
  1: "Easy at rest",
  2: "Slight change in breathing",
  3: "Breathing a little faster but comfortable",
  4: "Breathing moderately heavier, still easy",
  5: "Breathing clearly harder but controlled",
  6: "Breathing hard, noticeable effort",
  7: "Very hard breathing, but manageable",
  8: "Very breathless, difficult to sustain",
  9: "Near-maximal breathlessness",
  10: "Maximal breathlessness, had to stop or recover",
};

export const MUSCULAR_DESCRIPTIONS: Record<number, string> = {
  0: "Very little muscle effort",
  1: "Very little muscle effort",
  2: "Light muscle effort",
  3: "Mild muscle effort",
  4: "Moderate muscle effort, comfortable",
  5: "Moderately hard muscle effort",
  6: "Hard muscle effort, noticeable fatigue",
  7: "Very hard muscle effort, but manageable",
  8: "Very heavy effort, difficult to sustain",
  9: "Near-maximal effort, close to muscular failure",
  10: "Maximal muscle effort, failure or could not continue",
};

export const NIGGLE_PAIN_RESPONSES = [
  "No pain at all",
  "Very mild discomfort",
  "Mild pain",
  "Noticeable pain",
  "Moderate pain",
  "Moderately strong pain",
  "Strong pain",
  "Very strong pain",
  "Extremely strong pain",
  "Unbearable pain",
];

export const NIGGLE_IMPACT_RESPONSES = [
  "No impact on performance",
  "Barely noticeable impact",
  "Slight impact on performance",
  "Noticeable impact",
  "Moderate impact on performance",
  "Significant impact",
  "Major impact on performance",
  "Severe impact",
  "Extremely limiting",
  "Cannot perform at all",
];

const NIGGLE_COLOR_SCALE = [
  "#22c55e",
  "#34d399",
  "#6ee7b7",
  "#a7f3d0",
  "#fde68a",
  "#fca5a5",
  "#fb7185",
  "#f87171",
  "#ef4444",
  "#dc2626",
];

export const getNiggleColor = (value: number): string =>
  NIGGLE_COLOR_SCALE[value - 1] || "#dc2626";

// Unfilled portion of the slider track — visible in both light and dark mode.
export const SLIDER_TRACK = "rgba(120,120,120,0.22)";

const Slider: React.FC<{
  label: string;
  question: string;
  value: number;
  onChange: (v: number) => void;
  description: string;
  min?: number;
  max?: number;
  fillColor?: string; // solid colour for the filled track + value badge
}> = ({ label, question, value, onChange, description, min = 0, max = 10, fillColor }) => {
  const color = fillColor ?? getEffortColor(value);
  const percent = ((value - min) / (max - min)) * 100;
  const background = `linear-gradient(to right, ${color} 0%, ${color} ${percent}%, ${SLIDER_TRACK} ${percent}%, ${SLIDER_TRACK} 100%)`;
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <p className="text-xs font-medium text-base-content/80">{label}</p>

      </div>
      <div className="bg-base-200 p-3 rounded-md mb-2">
        <p className="text-xs text-base-content/70">{question}</p>
        <p className="text-md text-base-content font-bold">{description}</p>
      </div>

      <div className="flex space-x-2 items-center">
              <input
        type="range"
        min={min}
        max={max}
        step="1"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="tl-slider w-full"
        style={{ background }}
      />
                <span
          className="font-geist text-base font-semibold tabular-nums px-2"
          style={{ color }}
        >
          {value}
        </span>
      </div>
    </div>
  );
};

export default Slider;
